import { skipToken } from '@apollo/client/react'
import { useIntersection } from '@mantine/hooks'
import { produce } from 'immer'
import {
  forwardRef,
  type RefCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { type GridComponents, VirtuosoGrid } from 'react-virtuoso'
import { useImmer } from 'use-immer'
import FluentCheckmarkCircle24Filled from '~icons/fluent/checkmark-circle-24-filled'
import FluentDismiss24Filled from '~icons/fluent/dismiss-24-filled'
import MediaPreview from 'src/components/Common/MediaPreview'
import { Preloader } from 'src/components/Preloader/Preloader'
import { UIButton } from 'src/components/ui/Button'
import Text from 'src/components/ui/Text'
import {
  useGetArtwork,
  useInfiniteListArtworks,
} from 'src/services/apollo/artwork'
import type {
  ArtworkPreviewFragment,
  GenerationModelType,
} from 'src/services/apollo/generated/graphql'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import { getNodesFromConnection } from 'src/utils/graphql'
import { isLora } from 'src/utils/model'
import {
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
  UIDialogResponsive,
} from '../ui/Modal'

const artworkCache = new Map<string, ArtworkPreviewFragment>()

export type ArtworkPickerRef = {
  open: () => void
}

export type ArtworkPickerProps<M extends boolean> = {
  modelVersionId?: string
  modelType?: GenerationModelType
  value?: M extends true ? string[] : string
  multiple?: M
  min?: number
  max?: number
  onSubmit?: (value: M extends true ? string[] : string) => void
  onClose?: () => void
}

export default forwardRef<ArtworkPickerRef, ArtworkPickerProps<boolean>>(
  function ArtworkPicker(props, ref) {
    const { min = 1, max = Infinity } = props

    const { me } = useMe()
    const [open, setOpen] = useState(false)

    /*
     * Artwork data
     */

    const { data, loading, fetchMore, loadingMore } = useInfiniteListArtworks(
      me && open
        ? {
            authorId: me.id,
            [isLora(props.modelType) ? 'loraVersionId' : 'modelVersionId']:
              props.modelVersionId,
            orderBy: '-createdAt',
            first: 5,
          }
        : skipToken,
    )
    const havingMore = useMemo(
      () => data?.pageInfo?.hasNextPage ?? true,
      [data],
    )
    const artworks = useMemo(() => getNodesFromConnection(data) ?? [], [data])

    /*
     * Scroll to load
     */

    const [container, setContainer] = useState<HTMLElement | null>(null)
    const { ref: sentinelRef, entry: intersection } = useIntersection({
      root: container,
    })

    useEffect(() => {
      if (open && intersection?.isIntersecting && havingMore && !loading) {
        fetchMore({
          variables: {
            after: data?.pageInfo?.endCursor,
          },
        })
      }
    }, [
      havingMore,
      intersection?.isIntersecting,
      loading,
      open,
      data?.pageInfo,
      fetchMore,
    ])

    /*
     * Selection
     */

    const [selection, updateSelection] = useImmer(() => new Set<string>())

    function selectArtwork(artwork: ArtworkPreviewFragment) {
      const id = artwork.id
      if (props.multiple) {
        if (selection.has(id)) {
          selection.size > min &&
            updateSelection(selected => {
              selected.delete(id)
            })
        } else {
          selection.size < max &&
            updateSelection(selected => {
              selected.add(id)
            })
          if (artwork.media && !artworkCache.has(id)) {
            artworkCache.set(id, artwork)
          }
        }
      } else {
        props.onSubmit?.(id)
      }
    }

    /*
     * ---
     */

    function close() {
      setOpen(false)
      props.onClose?.()
      updateSelection(() => new Set())
    }

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          if (props.multiple && props.value) {
            updateSelection(new Set(props.value as string[]))
          }
          setOpen(true)
        },
      }),
      [props.multiple, props.value, updateSelection],
    )

    /*
     * Render
     */

    if (!me) return null

    return (
      <UIDialogResponsive
        open={open}
        onClose={close}
        className="text-sm mmd:w-[calc(100vw-64px)]"
      >
        <UIDialogHeader
          heading={
            <Text
              i18nKey="artwork:artwork-picker.title"
              values={{ count: props.multiple ? 2 : 1 }}
            />
          }
        ></UIDialogHeader>
        <UIDialogContent>
          <VirtuosoGrid
            data={artworks}
            context={
              {
                sentinelRef,
                havingMore,
                loading: loading || loadingMore,
              } satisfies VirtuosoContext
            }
            style={{ height: 600 }}
            scrollerRef={setContainer}
            components={gridComponents}
            listClassName="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-1"
            itemContent={(_, artwork) => (
              <div className="relative aspect-square">
                <MediaPreview
                  mediaId={artwork.mediaId}
                  thumbnail
                  className={cx(
                    'size-full rounded-md transition ease-in-out',
                    selection.has(artwork.id) && 'opacity-80 scale-90',
                  )}
                  onClick={() => selectArtwork(artwork)}
                />
                {selection.has(artwork.id) && (
                  <FluentCheckmarkCircle24Filled className="absolute inset-0 m-auto size-6 text-purple-600 bg-white rounded-full pointer-events-none" />
                )}
              </div>
            )}
          />
          {props.multiple && (
            <SelectionList
              selection={selection}
              onChange={updateSelection}
              max={props.max}
            />
          )}
        </UIDialogContent>
        <UIDialogFooter>
          <UIButton onPress={close}>
            <Text i18nKey="action:cancel" />
          </UIButton>
          {props.multiple && (
            <UIButton
              variant="filled"
              onPress={() => {
                props.onSubmit?.(Array.from(selection.keys()))
                close()
              }}
            >
              <Text i18nKey="action:confirm" />
            </UIButton>
          )}
        </UIDialogFooter>
      </UIDialogResponsive>
    )
  },
)

type VirtuosoContext = {
  sentinelRef: RefCallback<HTMLDivElement>
  havingMore: boolean
  loading: boolean
}

const gridComponents = {
  Footer: ({ context }) => (
    <div
      ref={context?.sentinelRef}
      className="mt-1 text-center text-sm text-neutral-500"
    >
      {!context || context.loading ? (
        <div
          className="mx-auto overflow-hidden"
          style={{ width: 42, height: 14 }}
        >
          <Preloader inline />
        </div>
      ) : !context.havingMore ? (
        <Text i18nKey="common:list-reached-the-end" />
      ) : null}
    </div>
  ),
} satisfies GridComponents<VirtuosoContext>

function SelectionList(props: {
  selection: Set<string>
  onChange?: (selection: Set<string>) => void
  max?: number
}) {
  return (
    <div className="mt-4 flex gap-1">
      <div className="grid place-content-center size-12 border border-zinc-300 dark:border-zinc-600 rounded">
        {props.selection.size}
        {Number(props.max) > 0 && `/${props.max}`}
      </div>
      {Array.from(props.selection).map(artworkId => (
        <SelectionItem
          key={artworkId}
          artworkId={artworkId}
          onRemove={() =>
            props.onChange?.(
              produce(props.selection, selection => {
                selection.delete(artworkId)
              }),
            )
          }
        />
      ))}
    </div>
  )
}

function SelectionItem(props: { artworkId: string; onRemove: () => void }) {
  const { data: artwork = artworkCache.get(props.artworkId) } = useGetArtwork(
    artworkCache.has(props.artworkId) ? skipToken : props.artworkId,
  )

  if (!artworkCache.has(props.artworkId) && artwork) {
    artworkCache.set(props.artworkId, artwork)
  }

  return (
    <div className="group/item relative">
      <MediaPreview
        mediaId={artwork?.mediaId}
        thumbnail
        className="size-12 rounded group-hover/item:opacity-50"
        onClick={props.onRemove}
      />
      <FluentDismiss24Filled className="absolute inset-0 m-auto size-6 text-black dark:text-white invisible group-hover/item:visible pointer-events-none" />
    </div>
  )
}
